import { Check, Circle } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { Avatar } from '@/components/ui/Avatar'
import { QuoteStatusPill, StatusPill } from '@/components/ui/StatusPill'
import { cn } from '@/lib/cn'
import { formatDate, formatNumber } from '@/lib/format'
import type { QuoteDetail } from '@/types/domain'

/* The right rail on both quote routes: what the reader needs SEEN while they
 * work on the left, rather than what they need to act on.
 *
 * These began as the three-step builder's rail — 340px of context that did not
 * scroll away beside a form the width of a wall. The builder is gone; the cards
 * that survived it are the ones that were never about a step. The CREATE page
 * shows QuoteContextCard, which says what the record is about to be, and the
 * DETAIL page shows QuoteMoneyCard, QuoteOfferFactsCard and, on a draft,
 * QuoteReadinessCard.
 *
 * Nothing here is a control. Every card is READ-ONLY, and every figure on them
 * is the server's (FR-1, FR-3) — a rail that let somebody edit the quote would
 * be a second form competing with the one they are filling in, and a rail that
 * computed a total would be a second opinion about the money.
 */

/** The rail's own section heading. Small caps, muted — it names the card
 *  without competing with the figures under it. */
function AsideHeading({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  return <p className={cn('asa-eyebrow mb-3.5', className)}>{children}</p>
}

function AsideCard({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <section
      className={cn(
        'bg-surface border-border-subtle rounded-[var(--radius-lg)] border px-5 py-4 shadow-xs',
        className,
      )}
    >
      {children}
    </section>
  )
}

/** One label-and-value line. The value is right-aligned against the label, so a
 *  column of them reads down the right edge the way the figures do. */
function AsideRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-text-muted shrink-0 text-[12.5px] font-light">
        {label}
      </dt>
      <dd className="text-text-body min-w-0 text-right text-[12.5px]">
        {children}
      </dd>
    </div>
  )
}

/* ── The create page ────────────────────────────────────────────────────────
 *
 * What the record IS, beside the form that is about to create it. The number is
 * the one thing on this card that does not exist yet, and saying so is the
 * point: FR-5 mints it server-side, so a salesperson who expects to choose one
 * finds out here rather than by hunting for the field.
 */
export function QuoteContextCard({ record }: { record?: QuoteDetail }) {
  const { t } = useTranslation()

  return (
    <AsideCard>
      <AsideHeading>{t('quotes.aside.quote')}</AsideHeading>

      <dl className="flex flex-col gap-3">
        <AsideRow label={t('quotes.aside.number')}>
          {record ? (
            <span className="asa-num text-text-heading font-semibold">
              {record.number}
            </span>
          ) : (
            <span className="text-text-muted font-light">
              {t('quotes.aside.numberPending')}
            </span>
          )}
        </AsideRow>

        <AsideRow label={t('quotes.aside.status')}>
          {record ? (
            <QuoteStatusPill status={record.status} />
          ) : (
            /* The status the create will produce, not one the record holds —
               every quote is born a draft (FR-5). Worded from the same
               catalogue the pill uses, so it cannot drift from the state it is
               predicting. */
            <StatusPill tone="neutral">{t('quoteStatus.draft')}</StatusPill>
          )}
        </AsideRow>

        {/* Only once there is a record. Before the create the owner is whoever
            is signed in, and printing a name this card did not read would be
            the rail inventing a fact — the one thing a read-only surface must
            not do. */}
        {record?.ownerName ? (
          <AsideRow label={t('quotes.aside.owner')}>
            <span className="flex items-center justify-end gap-2">
              <Avatar name={record.ownerName} size="sm" />
              <span className="text-text-body">{record.ownerName}</span>
            </span>
          </AsideRow>
        ) : null}
      </dl>
    </AsideCard>
  )
}

/* ── The detail page, on a draft ────────────────────────────────────────────
 *
 * What is still missing before the offer may go out — the send gate, split into
 * the three things it is actually made of.
 *
 * It is NOT the checklist and does not replace it. The checklist names a fault
 * on a named position and blocks the send (FR-3); this card names which THIRD
 * of the builder is unfinished, which is the question somebody standing on step
 * 2 is asking. The banner in the left column still carries the detail, because
 * a tick cannot say what is wrong with position 1.
 */
export function QuoteReadinessCard({ record }: { record: QuoteDetail }) {
  const { t } = useTranslation()

  const cargo = record.cargo ?? []
  const blocked = (record.checklist ?? []).length > 0

  const rows: readonly { key: string; label: string; done: boolean }[] = [
    {
      key: 'identity',
      label: t('quotes.aside.readyIdentity'),
      done: Boolean(record.clientId),
    },
    {
      key: 'routing',
      label: t('quotes.aside.readyRouting'),
      done: cargo.length > 0 && !blocked,
    },
    {
      key: 'pricing',
      label: t('quotes.aside.readyPricing'),
      done: (record.costLines ?? []).length > 0,
    },
  ]

  return (
    <AsideCard>
      <AsideHeading>{t('quotes.aside.readiness')}</AsideHeading>

      <ul className="flex flex-col gap-2.5">
        {rows.map((row) => (
          <li key={row.key} className="flex items-center gap-2">
            {/* Two different glyphs, not one glyph in two colours: roughly one
                man in twelve cannot separate the tick's green from the ring's
                grey, and this card is a list of yes and no. */}
            {row.done ? (
              <Check size={15} className="text-positive shrink-0" aria-hidden />
            ) : (
              <Circle
                size={15}
                className="text-text-faint shrink-0"
                aria-hidden
              />
            )}
            <span
              className={cn(
                'text-[12.5px]',
                row.done ? 'text-text-body' : 'text-text-muted font-light',
              )}
            >
              {row.label}
            </span>
          </li>
        ))}
      </ul>
    </AsideCard>
  )
}

/* ── The detail page ────────────────────────────────────────────────────────
 *
 * The money, on the one dark surface in the rail.
 *
 * These three figures were the builder's, and they came back to the page that
 * replaced it: the cost, what the client pays and the margin between them are
 * what a quote is FOR, and reading them meant opening the costing tab and
 * scrolling to a panel. The card carries them beside every tab instead. It is
 * the only inverse surface here, so the eye finds it without being told, and it
 * stays found while the tab underneath scrolls a screen past.
 *
 * It repeats three figures MarginPanel also prints, and knowingly. The panel
 * is the place they are CHANGED — it holds the markup field and the Apply
 * button, and it lives on one tab. This card is the place they are SEEN. The
 * two never disagree, because neither of them counts.
 *
 * NOTHING is computed. Every figure is `record.costTotals`, which the server
 * recomputes on every read (FR-1) — two surfaces that each add up the same
 * lines will eventually disagree by a rounding rule, and the one that disagrees
 * about the margin is the one somebody signs.
 */
export function QuoteMoneyCard({ record }: { record: QuoteDetail }) {
  const { t } = useTranslation()

  const totals = record.costTotals

  /* A negative margin is the one figure here that must not be read as good
     news. The inverse surface makes every figure look confident, so the sign is
     carried by more than the minus. */
  const margin = Number(totals?.marginPercent)
  const negative = Number.isFinite(margin) && margin < 0

  const currency = totals?.sell.currency

  /* Said only while there is nothing to say otherwise. Three zeroes on a quote
     nobody has costed yet look like a card that failed to load; the line names
     which tab turns them into figures. Once a single line exists the numbers
     speak for themselves, and the sentence would be shelf-space spent on a
     fact the reader can see. */
  const uncosted = (record.costLines ?? []).length === 0

  return (
    <section className="bg-surface-inverse text-text-on-inverse rounded-[var(--radius-lg)] px-5 py-4">
      <AsideHeading className="text-white/70">
        {t('quotes.aside.money')}
      </AsideHeading>

      <dl className="flex flex-col gap-3">
        <div className="flex items-baseline justify-between gap-3">
          <dt className="text-[12.5px] font-light text-white/70">
            {t('quotes.pricing.cost')}
          </dt>
          <dd className="asa-num text-[14px]">
            {formatNumber(totals?.buy.amount, 2)}
            {currency ? (
              <span className="ml-1 text-white/60">{currency}</span>
            ) : null}
          </dd>
        </div>

        <div className="flex items-baseline justify-between gap-3">
          {/* The COSTING's own word for this figure, not a second one of this
              card's: the same string labels the footer of the cost table and
              the markup panel, and one value under two names is two values to
              anybody reading quickly. */}
          <dt className="text-[12.5px] font-light text-white/70">
            {t('quotes.costLines.customerTotal')}
          </dt>
          <dd className="asa-num text-[14px]">
            {formatNumber(totals?.sell.amount, 2)}
            {currency ? (
              <span className="ml-1 text-white/60">{currency}</span>
            ) : null}
          </dd>
        </div>

        <div className="h-px bg-white/15" />

        <div className="flex items-baseline justify-between gap-3">
          {/* Named for its denominator, exactly as the markup panel names it:
              the percentage a reader types there is over COST, and this one is
              over revenue. The pair only reads correctly when each says which
              it used. */}
          <dt className="text-[12.5px] font-light text-white/70">
            {t('quotes.pricing.marginOverRevenue')}
          </dt>
          {/* A loss is not printed in the same voice as a profit. Blue is the
              ordinary reading; a negative margin is lifted onto its own light
              chip instead, because the inverse surface flatters every figure on
              it and a red that reads on white is invisible on ink. The minus
              sign carries the same fact for a reader who cannot separate the
              two — colour never says it alone. */}
          <dd
            className={cn(
              'asa-num text-[14px] font-semibold',
              negative
                ? 'bg-negative-soft text-negative rounded-full px-2 py-0.5'
                : 'text-blue-300',
            )}
          >
            {totals?.marginPercent === undefined
              ? t('common.empty')
              : `${formatNumber(totals.marginPercent, 2)}%`}
          </dd>
        </div>
      </dl>

      {uncosted ? (
        <p className="mt-3.5 text-[12px] leading-snug font-light text-white/55">
          {t('quotes.aside.moneyNote')}
        </p>
      ) : null}
    </section>
  )
}

/* ── The detail page ────────────────────────────────────────────────────────
 *
 * The terms as the offer will print them, beside the form that sets them.
 *
 * Four facts, and all four are ones a salesperson is asked about on the phone
 * while looking at this screen. They are the same values the terms card below
 * holds — this is not a second source, it is the subset that fits on a rail —
 * and an absent one renders as an em dash rather than as a blank, because "not
 * agreed yet" is an answer and an empty line is a rendering fault.
 */
export function QuoteOfferFactsCard({ record }: { record: QuoteDetail }) {
  const { t } = useTranslation()

  const dash = (value?: string) =>
    value && value.trim() !== '' ? value : t('common.empty')

  return (
    <AsideCard>
      <AsideHeading>{t('quotes.aside.offerCarries')}</AsideHeading>

      <dl className="flex flex-col gap-3">
        <AsideRow label={t('quotes.form.incoterm')}>
          {dash(record.incoterm)}
        </AsideRow>
        <AsideRow label={t('quotes.form.paymentTerms')}>
          {dash(record.paymentTerms)}
        </AsideRow>
        <AsideRow label={t('quotes.columns.validUntil')}>
          <span className="asa-num">{dash(formatDate(record.validUntil))}</span>
        </AsideRow>
      </dl>
    </AsideCard>
  )
}
